import Button from 'tailwind-components/Button/Button';
import CodeVerifier from './CodeVerifier';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import images from 'constants/images';
import style from './verification.module.scss';
import useCodeSender from '../useCodeSender';
import useCodeValidator from '../useCodeValidator';
import { useDispatch } from 'react-redux';
import { getUser } from 'services/user';
import { siteLoaded, siteLoading } from 'state/actions/site';

function VerificationCode({
  resolver = null,
  userCredentials: currentCredentials = null,
  phoneNumber = '',
  verificationId = '',
  setUserCredentials = null,
  reloadPortal = false,
  newPhone = false,
  newPhoneUpdate = false,
  /** when true, skip full-viewport wrapper (use when nested inside ResetMFA or another card). */
  embedded = false
}) {
  const dispatch = useDispatch();

  const { code, setCode, error, validate, isLoading, userCredentials } =
    useCodeValidator(
      verificationId,
      resolver,
      currentCredentials,
      newPhoneUpdate
    );

  const [seconds, setSeconds] = useState(30);
  const [done, setDone] = useState(false);
  const interval = useRef();

  const { sendCode, isLoading: sendingCode } = useCodeSender(
    'recaptcha-container',
    resolver
  );

  useEffect(() => {
    if (done) return;
    interval.current = setInterval(() => {
      setSeconds(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval.current);
  }, [done]);

  useEffect(() => {
    if (seconds <= 0) {
      clearInterval(interval.current);
      setDone(true);
    }
  }, [seconds]);

  async function logIn() {
    if (userCredentials) {
      if (reloadPortal) {
        dispatch(siteLoading());
        try {
          const userData = await getUser(userCredentials);
          dispatch({ type: 'SET_USER_DATA', payload: userData });
          dispatch({ type: 'LOG_IN' });
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(siteLoaded());
        }
      }
      if (typeof setUserCredentials === 'function') {
        setUserCredentials(userCredentials);
      }
    }
  }

  useEffect(() => {
    logIn();
  }, [userCredentials]);

  const compactLayout = embedded || (newPhone && newPhoneUpdate);
  const containerClass = compactLayout ? '' : style.login;
  const formClass = compactLayout ? '' : style.formContainer;

  return (
    <div className={containerClass}>
      <form
        className={formClass}
        onSubmit={e => {
          e.preventDefault();
          validate();
        }}
      >
        {!newPhone && (
          <>
            <img src={images.brand.fullLogo} alt="O'Neill Logo" />
            <p className={style.subTitle}>O&apos;Neill Host Portal Login</p>
          </>
        )}

        <p className={style.description}>
          We sent you a 6 digit code to{' '}
          <strong>{phoneNumber || 'your phone number'}</strong>.<br />
          Please enter it below to continue.
        </p>

        <CodeVerifier
          length={6}
          code={code}
          setCode={setCode}
          isLoading={isLoading}
          isError={!!error}
        />

        {error && <p className={style.errorMessage}>{error}</p>}

        <div className={style.submitButton}>
          <Button
            variant='primary'
            type='submit'
            id='recaptcha-container'
            text={newPhone ? 'Continue' : 'Submit and login'}
            isLoading={isLoading}
            disabled={code.some(digit => digit.trim() === '')}
          />
        </div>

        <div className={style.resendSection}>
          <p className={style.resendText}>
            {done
              ? "Didn't get the code? Request a new one."
              : `You can request a new code in ${seconds} seconds.`}
          </p>
          <Button
            variant='secondary'
            isLoading={sendingCode}
            disabled={!done}
            text='Resend Code'
            onClick={async e => {
              e.preventDefault();
              try {
                await sendCode();
                setSeconds(15);
                setDone(false);
              } catch (error) {
                console.error(error);
              }
            }}
          />
        </div>
      </form>
    </div>
  );
}

VerificationCode.propTypes = {
  resolver: PropTypes.object,
  userCredentials: PropTypes.object,
  phoneNumber: PropTypes.string,
  verificationId: PropTypes.string,
  setVerificationId: PropTypes.func,
  setUserCredentials: PropTypes.func,
  reloadPortal: PropTypes.bool,
  newPhone: PropTypes.bool,
  newPhoneUpdate: PropTypes.bool,
  embedded: PropTypes.bool
};

export default VerificationCode;
